// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * V08b — cross-origin one-click ownership signing (verifier side).
 *
 * Opens a popup at the Cobault user-app's `/sign-verifier-challenge`
 * route and awaits a `postMessage` carrying a DER-encoded ECDSA
 * signature over the canonical ownership-challenge message.
 *
 * Security properties this module upholds:
 *   1. The target user-app origin is derived from THIS SPA's own origin
 *      via a hard-coded symmetric map (verifier origin → user-app
 *      origin). No env vars, no DNS indirection, no runtime discovery.
 *   2. Every incoming `message` event is gated on all of:
 *        - `event.origin === <expected user-app origin>`, exact match
 *        - `event.data.type === 'cobault-verifier-ownership-v1'`
 *        - `event.data.vault_id === <vault the caller asked to sign>`
 *        - `event.data.nonce === <nonce the caller asked to sign>`
 *      Anything that fails ANY of these is silently ignored. We
 *      deliberately do not surface "got a reply we didn't expect" in
 *      UI, to avoid leaking the existence of the listener to drive-by
 *      tabs.
 *   3. The listener is one-shot: it removes itself on success, timeout,
 *      popup-close, or AbortSignal.
 *   4. The popup URL is built with URL-encoded params so metadata
 *      characters in the vault/nonce/origin never alter the query
 *      structure.
 */

// ── Hard-coded symmetric origin map ────────────────────────────────
//
// Verifier origin (where THIS SPA runs) → user-app origin (where we
// send the user to sign). Exact-string equality on both sides. Add an
// entry here when a new environment hostname lands.

const VERIFIER_TO_USER_APP: Readonly<Record<string, string>> = {
  // Dev: vite serves the user-app SPA on :5173.
  'http://localhost:5174': 'http://localhost:5173',
  // M-series staging: verifier at verifier.staging.cobault.io, user-app
  // SPA at wrap.staging.cobault.io. Staging-verifier MUST point at the
  // staging user-app, never at production — a verifier-staging popup
  // arriving at the prod user-app would land on a different key/vault
  // universe and silently fail to sign.
  'https://verifier.staging.cobault.io': 'https://wrap.staging.cobault.io',
  // Legacy / mainnet: verifier at app.verifier.cobault.io, user-app
  // SPA at wrap.cobault.io (CloudFront/S3). Note: `app.cobault.io` is
  // the separate cobault-web operator surface, NOT the user-app — do
  // not send users there. If a distinct mainnet user-app hostname
  // lands, pin it explicitly — do not regex over the domain suffix.
  'https://app.verifier.cobault.io': 'https://wrap.cobault.io',
};

/**
 * Resolve the user-app origin for the verifier origin we're currently
 * running on. `null` if the current origin isn't in the map — callers
 * must treat this as "cross-origin signing not available here".
 */
export function userAppOriginForCurrentVerifier(
  verifierOrigin: string,
): string | null {
  return VERIFIER_TO_USER_APP[verifierOrigin] ?? null;
}

// ── URL builder ────────────────────────────────────────────────────

export interface BuildSignUrlArgs {
  /** The user-app origin resolved via `userAppOriginForCurrentVerifier`. */
  userAppOrigin: string;
  /** UUID of the vault we're proving control of. */
  vault: string;
  /** 64-char lowercase hex nonce issued by `POST /lookup/{id}/challenge`. */
  nonce: string;
  /** The verifier origin that the user-app sign page will postMessage
   *  back to. Typically `window.location.origin` at the caller site. */
  returnOrigin: string;
}

/**
 * Construct the popup URL. URL-encodes every param so the query
 * structure is stable even if a future parameter contains a metadata
 * character.
 */
export function buildSignUrl(args: BuildSignUrlArgs): string {
  const params = new URLSearchParams({
    vault: args.vault,
    nonce: args.nonce,
    return_origin: args.returnOrigin,
  });
  return `${args.userAppOrigin}/sign-verifier-challenge?${params.toString()}`;
}

// ── Reply envelope ─────────────────────────────────────────────────

/** Shape of the postMessage reply the user-app sign page posts back.
 *  Mirrored from `web/spa/src/pages/sign-verifier-challenge.tsx` —
 *  keep in sync. */
export interface OwnershipSignatureReply {
  type: 'cobault-verifier-ownership-v1';
  vault_id: string;
  nonce: string;
  signature_der_hex: string;
}

/** HW.11 (COB-331): the WS-H proof object a hardware (or any v2) session posts
 *  back. Opaque to this SPA — it is forwarded verbatim to `/prove-v2`, where
 *  the verifier's `app_signature::parse_proof` fully validates it. We only
 *  shape-guard `format` so a malformed reply is ignored like any other. */
export interface OwnershipProof {
  format: string;
  signature_der_hex?: string;
  signature_base64?: string;
}

/** HW.11: the v2 reply envelope, carrying a proof object instead of a bare
 *  DER hex. A Ledger cannot emit the `der-sha256-v1` signature the v1 envelope
 *  carries, so a hardware popup session posts this shape; the caller routes it
 *  to `/prove-v2`. Mirrored from user-app's
 *  `web/spa/src/pages/sign-verifier-challenge.tsx` — keep in sync. */
export interface OwnershipProofReply {
  type: 'cobault-verifier-ownership-v2';
  vault_id: string;
  nonce: string;
  proof: OwnershipProof;
}

function isOwnershipSignatureReplyFor(
  value: unknown,
  vault: string,
  nonce: string,
): value is OwnershipSignatureReply {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    v.type === 'cobault-verifier-ownership-v1' &&
    typeof v.vault_id === 'string' &&
    v.vault_id === vault &&
    typeof v.nonce === 'string' &&
    v.nonce === nonce &&
    typeof v.signature_der_hex === 'string' &&
    /^[0-9a-f]+$/.test(v.signature_der_hex)
  );
}

function isOwnershipProofReplyFor(
  value: unknown,
  vault: string,
  nonce: string,
): value is OwnershipProofReply {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (
    v.type !== 'cobault-verifier-ownership-v2' ||
    v.vault_id !== vault ||
    v.nonce !== nonce ||
    typeof v.proof !== 'object' ||
    v.proof === null
  ) {
    return false;
  }
  // Shape-guard the envelope only; the verifier's /prove-v2 does the real
  // WS-H validation (der-sha256-v1 / bsm-bip137-v1, low-S, recovery).
  const proof = v.proof as Record<string, unknown>;
  return typeof proof.format === 'string';
}

// ── Popup flow ─────────────────────────────────────────────────────

export type RequestSignatureResult =
  | { kind: 'ok'; signatureDerHex: string }
  /** HW.11: a hardware session returned a WS-H proof object instead of a bare
   *  DER hex. The caller posts this to `/prove-v2` rather than `/prove`. */
  | { kind: 'ok-proof'; proof: OwnershipProof }
  /** The browser blocked `window.open`. Caller should render a tab-link
   *  pointing at `fallbackUrl` so the user can complete the flow in a
   *  new tab themselves. */
  | { kind: 'popup-blocked'; fallbackUrl: string }
  /** No reply within the timeout window. */
  | { kind: 'timeout' }
  /** The user closed the popup before signing. */
  | { kind: 'popup-closed' }
  /** AbortSignal fired. */
  | { kind: 'aborted' }
  /** We can't run this flow on the current origin (no entry in the
   *  symmetric origin map). */
  | { kind: 'not-configured' };

export interface RequestSignatureArgs {
  vault: string;
  nonce: string;
  /** The verifier origin that the user-app sign page will postMessage
   *  back to. Defaults to `window.location.origin`. */
  returnOrigin?: string;
  /** Override the current verifier origin used to resolve the user-app
   *  origin. Defaults to `window.location.origin`. Exposed primarily for
   *  tests — production callers should rely on the default. */
  verifierOrigin?: string;
  /** Popup width/height in px. Match the user-app sign page's compact
   *  layout. */
  width?: number;
  height?: number;
  /** Timeout in ms. Default 90 000 (90s). */
  timeoutMs?: number;
  /** Abort signal — if aborted before a reply arrives, the flow resolves
   *  `{ kind: 'aborted' }` and cleans up. */
  signal?: AbortSignal;
  /** Window.open override (used by tests). Defaults to `window.open`. */
  openWindow?: (
    url: string,
    name: string,
    features: string,
  ) => Window | null;
  /** MessageTarget override (used by tests). Defaults to `window`. */
  messageTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

const DEFAULT_TIMEOUT_MS = 90_000;
const DEFAULT_WIDTH = 480;
const DEFAULT_HEIGHT = 640;

/**
 * Request an ownership signature from the Cobault user-app SPA.
 *
 * Opens a popup at the user-app's `/sign-verifier-challenge`, registers
 * a one-shot `message` listener on `window`, and resolves with the DER
 * signature on a matched reply. Never rejects — all failure paths
 * resolve with a discriminated-union kind so call sites handle errors
 * in the UI instead of crashing on an unhandled rejection.
 */
export function requestSignatureFromUserApp(
  args: RequestSignatureArgs,
): Promise<RequestSignatureResult> {
  const verifierOrigin = args.verifierOrigin ?? window.location.origin;
  const returnOrigin = args.returnOrigin ?? window.location.origin;
  const userAppOrigin = userAppOriginForCurrentVerifier(verifierOrigin);
  if (!userAppOrigin) {
    return Promise.resolve({ kind: 'not-configured' });
  }

  const url = buildSignUrl({
    userAppOrigin,
    vault: args.vault,
    nonce: args.nonce,
    returnOrigin,
  });

  const open = args.openWindow ?? ((u, n, f) => window.open(u, n, f));
  const target = args.messageTarget ?? window;
  const timeoutMs = args.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const width = args.width ?? DEFAULT_WIDTH;
  const height = args.height ?? DEFAULT_HEIGHT;
  const features = `popup,width=${width},height=${height}`;

  const popup = open(url, 'cobault-verifier-ownership', features);
  if (!popup) {
    return Promise.resolve({ kind: 'popup-blocked', fallbackUrl: url });
  }

  return new Promise<RequestSignatureResult>((resolve) => {
    let settled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let popupWatchdog: ReturnType<typeof setInterval> | null = null;

    function cleanup(): void {
      if (settled) return;
      settled = true;
      target.removeEventListener('message', onMessage);
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      if (popupWatchdog !== null) clearInterval(popupWatchdog);
      if (args.signal) {
        args.signal.removeEventListener('abort', onAbort);
      }
    }

    function onMessage(ev: MessageEvent): void {
      if (ev.origin !== userAppOrigin) return;
      // v1 (software / Web3Auth): bare DER hex → /prove.
      if (isOwnershipSignatureReplyFor(ev.data, args.vault, args.nonce)) {
        cleanup();
        try {
          popup?.close();
        } catch {
          /* ignore */
        }
        resolve({
          kind: 'ok',
          signatureDerHex: (ev.data as OwnershipSignatureReply)
            .signature_der_hex,
        });
        return;
      }
      // v2 (hardware): WS-H proof object → /prove-v2.
      if (isOwnershipProofReplyFor(ev.data, args.vault, args.nonce)) {
        cleanup();
        try {
          popup?.close();
        } catch {
          /* ignore */
        }
        resolve({
          kind: 'ok-proof',
          proof: (ev.data as OwnershipProofReply).proof,
        });
        return;
      }
      // Anything else is silently ignored (see module header).
    }

    function onAbort(): void {
      cleanup();
      try {
        popup?.close();
      } catch {
        /* ignore */
      }
      resolve({ kind: 'aborted' });
    }

    function onTimeout(): void {
      cleanup();
      try {
        popup?.close();
      } catch {
        /* ignore */
      }
      resolve({ kind: 'timeout' });
    }

    target.addEventListener('message', onMessage);
    timeoutHandle = setTimeout(onTimeout, timeoutMs);

    if (args.signal) {
      if (args.signal.aborted) {
        onAbort();
        return;
      }
      args.signal.addEventListener('abort', onAbort);
    }

    // Watch for the user closing the popup without signing. A closed
    // popup that has not posted back within a short grace window
    // resolves `{ kind: 'popup-closed' }` so the UI can reset without
    // waiting for the 90s timeout. The grace window absorbs the race
    // between postMessage arriving and the popup's self-close after
    // success.
    const CLOSE_GRACE_MS = 600;
    let closedAt: number | null = null;
    popupWatchdog = setInterval(() => {
      if (settled) return;
      let closed = false;
      try {
        closed = popup.closed;
      } catch {
        // Some browsers throw on .closed access after cross-origin
        // navigation; treat as "still alive" and wait for the timeout.
        closed = false;
      }
      if (!closed) {
        closedAt = null;
        return;
      }
      if (closedAt === null) {
        closedAt = Date.now();
        return;
      }
      if (Date.now() - closedAt >= CLOSE_GRACE_MS) {
        cleanup();
        resolve({ kind: 'popup-closed' });
      }
    }, 200);
  });
}
